import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { IAI } from '@/modules/AiAgents/types';
import axiosClient from '@/shared/lib/axios';

/************************************ AI INITIALIZE SESSION *********************************************/
export const initializeSession = async (
  entityId?: number
): Promise<{ areaUnit: string }> => {
  const response = await axiosClient.post(
    `/ai/api/fields/session?entityId=${entityId}`
  );
  return response.data;
};

export const useInitializeSession = (entityId?: number) => {
  return useQuery({
    queryKey: ['initializeChat', entityId],
    queryFn: () => initializeSession(entityId),
    enabled: !!entityId,
  });
};

export const initializeAgronomistSession = async (
  entityId?: number,
  language?: string
): Promise<{
  id: string;
  title: string | null;
  tag: string;
  relativeTime: string;
}> => {
  const response = await axiosClient.post(
    `/ai/api/chats?entityId=${entityId}`,
    { language }
  );
  return response.data;
};

export const useInitializeAgronomistSession = () => {
  return useMutation({
    mutationKey: ['initializeAgronomistChat'],
    mutationFn: ({
      entityId,
      language,
    }: {
      entityId?: number;
      language?: string;
    }) => initializeAgronomistSession(entityId, language),
  });
};

/************************************ AI SEND MESSAGE *********************************************/
export const sendChatMessage = async ({
  entityId,
  data,
}: IAI.ISendMessageProps): Promise<IAI.IChatResponseSchema> => {
  const formData: FormData = new FormData();

  if (data.message) {
    formData.append('message', data.message);
  }
  if (data.file) {
    formData.append('file', data.file);
  }

  const response = await axiosClient.post(
    `/ai/api/fields/message?entityId=${entityId}`,
    formData
  );
  return response.data;
};

export const useSendChatMessage = () => {
  return useMutation({
    mutationKey: ['sendChatData'],
    mutationFn: sendChatMessage,
  });
};

// Agronomist send message

/************************************ UPDATE FIELDS TABLE *********************************************/
export const updateFieldsTable = async ({
  entityId,
  data,
}: IAI.ISubmitFieldsProps): Promise<IAI.IAIFieldProps[]> => {
  const response = await axiosClient.patch(
    `/ai/api/fields?entityId=${entityId}`,
    data
  );
  return response.data;
};

export const useUpdateFieldsTable = () => {
  return useMutation({
    mutationKey: ['updateFieldsTable'],
    mutationFn: updateFieldsTable,
  });
};
/************************************ SUBMIT FIELDS TABLE *********************************************/
export const submitFieldsTable = async ({
  entityId,
  data,
}: IAI.ISubmitFieldsProps) => {
  const response = await axiosClient.post(
    `/ai/api/fields/submit?entityId=${entityId}`,
    data
  );
  return response.data;
};

export const useSubmitFieldsTable = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['submitFieldsTable'],
    mutationFn: submitFieldsTable,
    onSuccess: () => {
      queryClient.invalidateQueries(['initializeChat']);
      queryClient.invalidateQueries(['getOnboardingStatus']);
      queryClient.invalidateQueries(['getHomeFields']);
    },
  });
};
/************************************ AI RESET SESSION *********************************************/
export const deleteChatData = async (entityId: number) => {
  const response = await axiosClient.delete(
    `/ai/api/fields/session?entityId=${entityId}`
  );
  return response;
};

export const useDeleteChat = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['deleteChatData'],
    mutationFn: deleteChatData,
    onSuccess: () => {
      queryClient.invalidateQueries(['initializeChat']);
    },
  });
};

/************************************ AI Agronomist Histrory *********************************************/
export const getAgronomistHistory = async (
  params: IAI.IAgronomistHistoryParams
) => {
  const response = (await axiosClient.get(`/ai/api/chats`, { params }))
    .data as IAI.IAgronomistHistoryResponse;
  return {
    items: response.sessions,
    nextPage:
      params?.pageNumber * params?.pageSize < response?.totalCount
        ? params?.pageNumber + 1
        : undefined,
  };
};

export const useGetAgronomistChatHistory = (
  params: IAI.IAgronomistHistoryParams
) => {
  return useInfiniteQuery({
    queryKey: ['getAgronomistHistory', params],
    queryFn: ({ pageParam }) =>
      getAgronomistHistory({
        ...params,
        pageNumber: pageParam ?? params.pageNumber,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,

    enabled: !!params.entityId,
  });
};

/************************************ AI Agronomist list conversation messages  *********************************************/
export const getConversationMessages = async (
  params: IAI.IAgronomistConversationMessageParams
) => {
  const response = (
    await axiosClient.get(`/ai/api/chats/${params.sessionId}/messages`, {
      params: {
        pageNumber: params.pageNumber,
        pageSize: params.pageSize,
      },
    })
  ).data as IAI.IAgronomistConversationMessagesResponse;
  return {
    items: response.messages,
    nextPage:
      params?.pageNumber * params?.pageSize < response?.totalCount
        ? params?.pageNumber + 1
        : undefined,
  };
};

export const useGetConversationMessages = (
  params: IAI.IAgronomistConversationMessageParams
) => {
  return useInfiniteQuery({
    queryKey: ['getAgronomistConversationMessages', params],
    queryFn: ({ pageParam }) =>
      getConversationMessages({
        ...params,
        pageNumber: pageParam ?? params.pageNumber,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!params.sessionId && !params.isInitialSession,
  });
};

/************************************ AI Agronomist Stagging files   *********************************************/
export const stageFile = async (sessionId: string, files: File[]) => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
  });

  const response = (
    await axiosClient.post(`/ai/api/chats/${sessionId}/files/upload`, formData)
  ).data as {
    stagedFiles: IAI.StagedFile[];
  };
  return response;
};

export const unStageFile = async (sessionId: string, fileId: string) => {
  return await axiosClient.delete(
    `/ai/api/chats/${sessionId}/files/${fileId}`,
    {}
  );
};

/************************************ AI Agronomist Stagging files   *********************************************/
export const getConversationTitle = async (
  sessionId?: string | null
): Promise<{ title: string }> => {
  const response = await axiosClient.get(`/ai/api/chats/${sessionId}/title`);
  return response.data;
};

export const useGetConversationTitle = () => {
  return useMutation({
    mutationKey: ['getChatTitle'],
    mutationFn: (sessionId: string) => getConversationTitle(sessionId),
  });
};
